import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppSelector, useAppDispatch } from '../redux/hooks'
import {
  fetchUsers,
  fetchUserStats,
  suspendUser,
  unsuspendUser,
  resetUserPassword,
  setFilters,
  clearError,
  UserListItem,
} from '../redux/reducers/usersSlice'
import {
  Users,
  Search,
  Filter,
  MoreVertical,
  Eye,
  Ban,
  CheckCircle,
  Key,
  Download,
  RefreshCw,
  AlertCircle,
  UserCheck,
  UserX,
  UserPlus,
  Calendar,
  Mail,
  Shield,
} from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import Pagination from '../components/Pagination'

const UserManagement: React.FC = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { users, loading, error, pagination, filters, stats } = useAppSelector((state) => state.users)
  const { user: currentAdmin } = useAppSelector((state) => state.auth)
  
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedStatus, setSelectedStatus] = useState<string>('all')
  const [showContextMenu, setShowContextMenu] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState('')
  
  // Initial fetch on mount
  useEffect(() => {
    dispatch(fetchUsers({
      page: 1,
      limit: 20,
      search: '',
      status: 'all',
      sortBy: 'createdAt',
      sortOrder: 'desc',
    }))
    dispatch(fetchUserStats())
  }, [dispatch])
  
  // Auto-dismiss success message
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('')
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [successMessage])
  
  // Handle search
  const handleSearch = () => {
    dispatch(fetchUsers({
      page: 1,
      limit: pagination.limit,
      search: searchQuery,
      status: selectedStatus as any,
      sortBy: filters.sortBy,
      sortOrder: filters.sortOrder,
    }))
  }
  
  // Handle refresh
  const handleRefresh = () => {
    handleSearch()
    dispatch(fetchUserStats())
  }
  
  // Handle filter change
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery !== filters.search || selectedStatus !== filters.status) {
        handleSearch()
      }
    }, 500)
    return () => clearTimeout(timer)
  }, [searchQuery, selectedStatus])
  
  const showSuccess = (message: string) => {
    setSuccessMessage(message)
  }
  
  const handleViewDetails = (user: UserListItem) => {
    navigate(`/users/${user.uuid}`)
    setShowContextMenu(null)
  }
  
  const handleResetPassword = async (user: UserListItem) => {
    if (confirm(`Reset password for ${user.name}? An email will be sent to the user.`)) {
      try {
        await dispatch(resetUserPassword({
          userId: user.id,
          sendEmail: true,
        })).unwrap()
        showSuccess('Password reset email sent successfully')
      } catch (error) {
        console.error('Failed to reset password:', error)
      }
    }
    setShowContextMenu(null)
  }
  
  const handleToggleStatus = async (user: UserListItem) => {
    try {
      if (user.status === 'suspended') {
        await dispatch(unsuspendUser(user.id)).unwrap()
        showSuccess('User account activated successfully')
      } else {
        const reason = prompt('Reason for suspension (optional):')
        await dispatch(suspendUser({ userId: user.id, reason: reason || undefined })).unwrap()
        showSuccess('User account suspended successfully')
      }
      handleRefresh()
    } catch (error) {
      console.error('Failed to toggle user status:', error)
    }
    setShowContextMenu(null)
  }
  
  const handleExport = () => {
    console.log('Export users')
    // TODO: Implement export functionality
  }
  
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-400">
            <CheckCircle className="w-3 h-3 mr-1" />
            Active
          </span>
        )
      case 'inactive':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-900/50 dark:text-gray-400">
            <UserX className="w-3 h-3 mr-1" />
            Inactive
          </span>
        )
      case 'suspended':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-400">
            <Ban className="w-3 h-3 mr-1" />
            Suspended
          </span>
        )
      default:
        return null
    }
  }
  
  return (
    <div className="space-y-4 px-3 sm:px-0">
      {/* Success Message */}
      {successMessage && (
        <div className="fixed top-4 right-4 z-50 bg-green-500 text-white px-3 py-2 rounded-md shadow-lg flex items-center space-x-2 animate-slide-in">
          <UserCheck className="w-4 h-4" />
          <span className="text-sm">{successMessage}</span>
        </div>
      )}
      
      {/* Error Message */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/50 rounded-md p-2 flex items-start space-x-2">
          <AlertCircle className="w-4 h-4 text-red-400 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-red-300">{error}</p>
            <button 
              onClick={() => dispatch(clearError())}
              className="text-xs text-red-400 underline mt-1"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}
      
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold tracking-tight text-foreground">User Management</h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">Manage and monitor user accounts</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-card border border-border rounded-lg p-3 sm:p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:flex lg:flex-row gap-2 sm:gap-3">
          {/* Search */}
          <div className="sm:col-span-2 lg:flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-9 w-full pl-10 pr-3 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          </div>

          {/* Status Filter */}
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="h-9 w-full lg:w-auto px-3 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="suspended">Suspended</option>
          </select>

          {/* Page Size Selector */}
          <select
            value={pagination.limit}
            onChange={(e) => dispatch(fetchUsers({ ...filters, limit: parseInt(e.target.value), page: 1 }))}
            className="h-9 w-full lg:w-auto px-3 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value={10}>10 / page</option>
            <option value={20}>20 / page</option>
            <option value={50}>50 / page</option>
            <option value={100}>100 / page</option>
          </select>

          {/* Action Buttons Group */}
          <div className="flex gap-2 sm:col-span-2 lg:col-span-1">
            {/* Refresh Button */}
            <button 
              onClick={handleRefresh}
              disabled={loading}
              className="h-9 flex-1 lg:flex-initial px-3 text-sm bg-background border border-border rounded-md hover:bg-muted transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
            
            {/* Export Button */}
            <button 
              onClick={handleExport}
              className="h-9 flex-1 lg:flex-initial px-3 text-sm bg-background border border-border rounded-md hover:bg-muted transition-colors flex items-center justify-center space-x-2"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Export</span>
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-card border border-border rounded-md p-3">
          <div className="flex items-center space-x-2">
            <div className="p-1.5 bg-blue-500/10 rounded-md">
              <Users className="w-4 h-4 text-blue-500" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Total Users</p>
              <p className="text-lg font-semibold text-foreground">{stats.totalUsers}</p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-md p-3">
          <div className="flex items-center space-x-2">
            <div className="p-1.5 bg-green-500/10 rounded-md">
              <UserCheck className="w-4 h-4 text-green-500" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Active</p>
              <p className="text-lg font-semibold text-foreground">{stats.activeUsers}</p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-md p-3">
          <div className="flex items-center space-x-2">
            <div className="p-1.5 bg-gray-500/10 rounded-md">
              <UserX className="w-4 h-4 text-gray-500" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Inactive</p>
              <p className="text-lg font-semibold text-foreground">{stats.inactiveUsers}</p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-md p-3">
          <div className="flex items-center space-x-2">
            <div className="p-1.5 bg-purple-500/10 rounded-md">
              <UserPlus className="w-4 h-4 text-purple-500" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">New This Month</p>
              <p className="text-lg font-semibold text-foreground">{stats.newUsersThisMonth}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-card border border-border rounded-md overflow-hidden">
        {/* Desktop Table */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  User
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Verified
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Registered
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Last Login
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Logins
                </th>
                <th className="px-4 py-2 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading && users.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-sm text-muted-foreground">
                    Loading users...
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-sm text-muted-foreground">
                    No users found
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr 
                    key={user.id} 
                    className="hover:bg-muted/50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/users/${user.uuid}`)}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center">
                          <span className="text-xs font-medium text-white">
                            {user.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground">{user.name}</p>
                          <p className="text-xs text-muted-foreground">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {getStatusBadge(user.status)}
                    </td>
                    <td className="px-4 py-3">
                      {user.emailVerified ? (
                        <Mail className="w-4 h-4 text-green-500" title="Email Verified" />
                      ) : (
                        <Mail className="w-4 h-4 text-gray-400" title="Email Not Verified" />
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {user.registeredAt ? (
                        <div>
                          <div className="text-sm text-foreground">
                            {(() => {
                              try {
                                const date = new Date(user.registeredAt)
                                return isNaN(date.getTime()) ? 'Invalid date' : date.toLocaleDateString()
                              } catch {
                                return 'Invalid date'
                              }
                            })()}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {(() => {
                              try {
                                const date = new Date(user.registeredAt)
                                return isNaN(date.getTime()) ? '' : formatDistanceToNow(date, { addSuffix: true })
                              } catch {
                                return ''
                              }
                            })()}
                          </div>
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">Unknown</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {user.lastLoginAt ? (
                        <div>
                          <div className="text-sm text-foreground">
                            {(() => {
                              try {
                                const date = new Date(user.lastLoginAt)
                                return isNaN(date.getTime()) ? 'Invalid date' : date.toLocaleDateString()
                              } catch {
                                return 'Invalid date'
                              }
                            })()}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {(() => {
                              try {
                                const date = new Date(user.lastLoginAt)
                                return isNaN(date.getTime()) ? '' : formatDistanceToNow(date, { addSuffix: true })
                              } catch {
                                return ''
                              }
                            })()}
                          </div>
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">Never</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-foreground">{user.loginCount || 0}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="relative">
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            setShowContextMenu(showContextMenu === user.id ? null : user.id)
                          }}
                          className="p-1 hover:bg-muted rounded-md transition-colors"
                        >
                          <MoreVertical className="w-4 h-4 text-muted-foreground" />
                        </button>
                        
                        {showContextMenu === user.id && (
                          <div className="absolute right-0 mt-2 w-48 bg-card border border-border rounded-md shadow-lg z-10">
                            <button
                              onClick={() => handleViewDetails(user)}
                              className="w-full text-left px-4 py-2 text-sm hover:bg-muted transition-colors flex items-center space-x-2"
                            >
                              <Eye className="w-4 h-4" />
                              <span>View Details</span>
                            </button>
                            <button
                              onClick={() => handleResetPassword(user)}
                              className="w-full text-left px-4 py-2 text-sm hover:bg-muted transition-colors flex items-center space-x-2"
                            >
                              <Key className="w-4 h-4" />
                              <span>Reset Password</span>
                            </button>
                            <button
                              onClick={() => handleToggleStatus(user)}
                              className="w-full text-left px-4 py-2 text-sm hover:bg-muted transition-colors flex items-center space-x-2"
                            >
                              {user.status === 'suspended' ? (
                                <>
                                  <CheckCircle className="w-4 h-4" />
                                  <span>Activate Account</span>
                                </>
                              ) : (
                                <>
                                  <Ban className="w-4 h-4" />
                                  <span>Suspend Account</span>
                                </>
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Mobile Card View */}
        <div className="lg:hidden">
          {loading && users.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
              Loading users...
            </div>
          ) : users.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
              No users found
            </div>
          ) : (
            <div className="divide-y divide-border">
              {users.map((user) => (
                <div 
                  key={user.id}
                  className="p-4 hover:bg-muted/50 transition-colors"
                  onClick={() => navigate(`/users/${user.uuid}`)}
                >
                  {/* Card Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-medium text-white">
                          {user.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{user.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                      </div>
                    </div>
                    {getStatusBadge(user.status)}
                  </div>
                  
                  {/* Card Body */}
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-xs text-muted-foreground">Verified</span>
                      <div className="flex items-center mt-1">
                        {user.emailVerified ? (
                          <Mail className="w-4 h-4 text-green-500" />
                        ) : (
                          <Mail className="w-4 h-4 text-gray-400" />
                        )}
                        <span className="ml-1 text-xs">{user.emailVerified ? 'Yes' : 'No'}</span>
                      </div>
                    </div>
                    
                    <div>
                      <span className="text-xs text-muted-foreground">Logins</span>
                      <p className="text-sm font-medium mt-1">{user.loginCount || 0}</p>
                    </div>
                    
                    <div>
                      <span className="text-xs text-muted-foreground">Registered</span>
                      <p className="text-xs mt-1">
                        {(() => {
                          try {
                            const date = new Date(user.registeredAt)
                            return isNaN(date.getTime()) ? 'N/A' : formatDistanceToNow(date, { addSuffix: true })
                          } catch {
                            return 'N/A'
                          }
                        })()}
                      </p>
                    </div>
                    
                    <div>
                      <span className="text-xs text-muted-foreground">Last Login</span>
                      <p className="text-xs mt-1">
                        {user.lastLoginAt ? formatDistanceToNow(new Date(user.lastLoginAt), { addSuffix: true }) : 'Never'}
                      </p>
                    </div>
                  </div>
                  
                  {/* Card Actions */}
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        navigate(`/users/${user.uuid}`)
                      }}
                      className="text-sm text-primary hover:underline"
                    >
                      View Details
                    </button>
                    
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setShowContextMenu(showContextMenu === user.id ? null : user.id)
                      }}
                      className="p-1 hover:bg-muted rounded"
                    >
                      <MoreVertical className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Pagination */}
        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          totalItems={pagination.total}
          itemsPerPage={pagination.limit}
          onPageChange={(page) => dispatch(fetchUsers({ ...filters, page }))}
        />
      </div>

      {/* User Details Modal */}
    </div>
  )
}

export default UserManagement